import { createSelector } from 'reselect'
import config from 'constants/config'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getOptimizelyFeatureFlagVariation } from 'lib/optimizely/optimizelyUtils'
import { isSpoofed } from 'selectors/isSpoofed'
import { PASSPORT_CARD_MAX_ADULTS } from 'constants/insurance'
import { getToursCruisesAdultCount, isStandaloneToursCruisesCart } from 'checkout/lib/insurance/passportCardToursCruises'
import { isPassportCardStartDateWithinAllowedRange } from 'checkout/Components/CheckoutSteps/Review/PassportCardInsurance/passportCardUtils'
import getInsuranceDatesFromCart from './getInsuranceDatesFromCart'
import { hasPassportCardEligibleItems } from '../view/itemViews/getPassportCardQuoteItems'
import { getPassportCardDestinationCountriesFromCart } from '../view/getInsuranceDestinationCountriesFromCart'
/**
 * Non-spoofed customer-facing entry point that offers PassportCard instead of
 * XCover for eligible standalone tour and cruise bookings. PassportCard is capped
 * at 2 adults, so larger parties fall back to XCover. Enabled regions and verticals
 * are config-driven (PASSPORT_CARD_ENABLED_REGIONS / PASSPORT_CARD_ENABLED_ITEM_TYPES).
 * The Optimizely feature flag controls the rollout across every environment.
 */
const isPassportCardInsuranceSupportedForToursCruises = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getInsuranceDatesFromCart(state)?.startDate,
  (state: App.State) => hasPassportCardEligibleItems(state),
  (state: App.State) => getPassportCardDestinationCountriesFromCart(state),
  (state: App.State) => getOptimizelyFeatureFlagVariation(
    state,
    OptimizelyFeatureFlags.croPassportCardToursCruisesEnabled,
  ),
  (
    regionCode,
    spoofed,
    cartItems,
    checkoutStartDate,
    cartHasEligibleItems,
    destinationCountries,
    isFeatureEnabled,
  ): boolean => {
    if (spoofed) return false
    if (!isFeatureEnabled) return false
    if (!isEnabledForFeature(config.PASSPORT_CARD_ENABLED_REGIONS, regionCode)) return false
    if (!isStandaloneToursCruisesCart(cartItems)) return false
    if (getToursCruisesAdultCount(cartItems) > PASSPORT_CARD_MAX_ADULTS) return false
    if (!isPassportCardStartDateWithinAllowedRange(checkoutStartDate)) return false
    // Without destination countries there is nothing to quote - svc-insurance requires a
    // non-empty destination_countries - so keep the section hidden rather than sending a
    // request that cannot succeed. Read from the PassportCard variant, which unlike XCover
    // still derives countries for a tour whose departure is not guaranteed.
    if (!destinationCountries.length) return false
    return cartHasEligibleItems
  },
)

export default isPassportCardInsuranceSupportedForToursCruises
